import { api } from './api-client';

// The admin identity endpoints are dashboard endpoints under `/api/admin/*`
// (session-cookie auth, gated by `requirePlatformAdmin` / active-org
// membership). They use the same `api` client and `/api` base path as every
// other admin-app → backend call. Paths below are relative to BASE_URL
// (`/api`), so `/admin/...` resolves to `/api/admin/...`.

// ---------- Types ----------

export type IdentitySummary = {
  id: string;
  organization_id: string;
  primary_user_id: string | null;
  first_seen_at: string;
  last_seen_at: string;
};

export type IdentityAlias = {
  id: string;
  identity_id: string;
  organization_id: string;
  alias_type: string;
  alias_value: string;
  source_project_id: string | null;
  is_primary: boolean;
  created_at: string;
};

export type IdentityTraits = Record<string, unknown>;

export type IdentityDetail = {
  identity: IdentitySummary;
  aliases: IdentityAlias[];
  traits: IdentityTraits;
};

export type IdentityEvent = {
  id: string;
  name: string;
  message?: string | null;
  userId?: string | null;
  project?: string | null;
  channel?: string | null;
  createdAt: string;
  metadata?: Record<string, unknown> | null;
};

export type IdentityEventsResponse = {
  events: IdentityEvent[];
  next_cursor: string | null;
};

export type IdentitiesListResponse = {
  identities: IdentitySummary[];
  next_cursor: string | null;
};

export type IdentityConflict = {
  audit_id: string;
  organization_id: string;
  project_id: string | null;
  anonymous_id: string | null;
  user_id: string | null;
  created_at: string;
  payload: Record<string, unknown>;
  user_identity: IdentitySummary | null;
  anonymous_identity: IdentitySummary | null;
};

export type IdentityConflictsResponse = {
  conflicts: IdentityConflict[];
  next_cursor: string | null;
};

export type ActiveIdentifiedUsersUsage = {
  period: string;
  activeIdentifiedUserCount: number;
  organizationId: string;
};

export type ProjectIdentitySettings = {
  project_id: string;
  organization_id: string;
  identity_scope_mode: 'organization' | 'project_only';
};

export type IdentityVerificationConfig = {
  project_id: string;
  mode: 'track_identify' | 'identify_only' | 'secure';
  issuer: string | null;
  audience: string | null;
  jwks_url: string | null;
  algorithms: string[];
};

export type ListIdentitiesParams = {
  organizationId: string;
  aliasType?: string;
  aliasValue?: string;
  limit?: number;
  cursor?: string | null;
};

// Per-category permission is a tri-state: true (granted), false (denied), or
// null (no source ever spoke for this category → falls through to the org's
// consentBehavior). The UI renders these as granted / denied / unset.
export type IdentityConsent = {
  categories: {
    analytics: boolean | null;
    functional: boolean | null;
    marketing: boolean | null;
  };
  signals: {
    gpc: boolean | null;
    doNotSellShare: boolean | null;
    gppDetected: boolean | null;
  };
  source: string;
  updated_at: string;
};

export type IdentityConsentResponse = {
  consent: IdentityConsent | null;
};

export type ConsentHistoryEntry = {
  id: string;
  source: string;
  changes: Record<string, unknown>;
  created_at: string;
};

export type ConsentHistoryResponse = {
  history: ConsentHistoryEntry[];
  next_offset: number | null;
};

// ---------- Service ----------

type SuccessEnvelope<T> = { data: T };

export const IdentitiesService = {
  listIdentities: async ({
    organizationId,
    aliasType,
    aliasValue,
    limit,
    cursor,
  }: ListIdentitiesParams): Promise<IdentitiesListResponse> => {
    const params = new URLSearchParams();
    params.set('organization_id', organizationId);
    if (aliasType) params.set('alias_type', aliasType);
    if (aliasValue) params.set('alias_value', aliasValue);
    if (limit) params.set('limit', String(limit));
    if (cursor) params.set('cursor', cursor);
    const result = await api.get<SuccessEnvelope<IdentitiesListResponse>>(
      `/admin/identities?${params.toString()}`,
    );
    return result.data;
  },

  getIdentity: async (identityId: string): Promise<IdentityDetail> => {
    const result = await api.get<SuccessEnvelope<IdentityDetail>>(
      `/admin/identities/${identityId}`,
    );
    return result.data;
  },

  // Org-wide recent events for an identity (across all projects in its org).
  getIdentityEvents: async (
    identityId: string,
    params?: { cursor?: string | null; limit?: number },
  ): Promise<IdentityEventsResponse> => {
    const search = new URLSearchParams();
    if (params?.limit) search.set('limit', String(params.limit));
    if (params?.cursor) search.set('cursor', params.cursor);
    const qs = search.toString();
    const result = await api.get<SuccessEnvelope<IdentityEventsResponse>>(
      `/admin/identities/${identityId}/events${qs ? `?${qs}` : ''}`,
    );
    return result.data;
  },

  // Current consent state for an identity (categories, signals, source).
  getIdentityConsent: async (
    identityId: string,
  ): Promise<IdentityConsentResponse> => {
    const result = await api.get<SuccessEnvelope<IdentityConsentResponse>>(
      `/admin/identities/${identityId}/consent`,
    );
    return result.data;
  },

  // Paginated (offset) consent-history timeline, newest first.
  getIdentityConsentHistory: async (
    identityId: string,
    params?: { offset?: number; limit?: number },
  ): Promise<ConsentHistoryResponse> => {
    const search = new URLSearchParams();
    if (params?.limit) search.set('limit', String(params.limit));
    if (params?.offset) search.set('offset', String(params.offset));
    const qs = search.toString();
    const result = await api.get<SuccessEnvelope<ConsentHistoryResponse>>(
      `/admin/identities/${identityId}/consent-history${qs ? `?${qs}` : ''}`,
    );
    return result.data;
  },

  attachAlias: async (input: {
    identity_id: string;
    type: string;
    value: string;
    source_project_id?: string | null;
  }): Promise<void> => {
    await api.post('/admin/attach-alias', input);
  },

  unlinkIdentity: async (identityId: string): Promise<void> => {
    await api.delete(`/admin/identities/${identityId}`);
  },

  detachAlias: async (aliasId: string): Promise<void> => {
    await api.delete(`/admin/aliases/${aliasId}`);
  },

  setPrimaryAlias: async (aliasId: string): Promise<void> => {
    await api.put(`/admin/aliases/${aliasId}/primary`);
  },

  updateIdentityTraits: async (
    identityId: string,
    traits: Record<string, unknown>,
  ): Promise<void> => {
    await api.put(`/admin/identities/${identityId}/traits`, { traits });
  },

  mergeIdentities: async (input: {
    primary_id: string;
    secondary_id: string;
    // When triaging a captured conflict, the conflict's audit id so the
    // backend can mark that specific conflict resolved.
    conflict_audit_id?: string | null;
  }): Promise<void> => {
    await api.post('/admin/merge-identities', input);
  },

  listConflicts: async (params: {
    organizationId: string;
    resolved?: boolean;
    limit?: number;
    cursor?: string | null;
  }): Promise<IdentityConflictsResponse> => {
    const search = new URLSearchParams();
    search.set('organization_id', params.organizationId);
    if (params.resolved !== undefined) {
      search.set('resolved', params.resolved ? 'true' : 'false');
    }
    if (params.limit) search.set('limit', String(params.limit));
    if (params.cursor) search.set('cursor', params.cursor);
    const result = await api.get<
      SuccessEnvelope<IdentityConflictsResponse>
    >(`/admin/identity-conflicts?${search.toString()}`);
    return result.data;
  },

  leaveConflictSeparate: async (auditId: string): Promise<void> => {
    await api.patch(`/admin/identity-conflicts/${auditId}`, {
      action: 'leave_separate',
    });
  },

  // --- Project identity settings ---
  getProjectIdentitySettings: async (
    projectId: string,
  ): Promise<ProjectIdentitySettings> => {
    const result = await api.get<
      SuccessEnvelope<ProjectIdentitySettings>
    >(`/admin/projects/${projectId}/identity-settings`);
    return result.data;
  },

  updateProjectIdentitySettings: async (input: {
    projectId: string;
    identity_scope_mode: 'organization' | 'project_only';
  }): Promise<ProjectIdentitySettings> => {
    const result = await api.post<
      SuccessEnvelope<ProjectIdentitySettings>
    >(`/admin/projects/${input.projectId}/identity-settings`, {
      identity_scope_mode: input.identity_scope_mode,
    });
    return result.data;
  },

  getIdentityVerificationConfig: async (
    projectId: string,
  ): Promise<IdentityVerificationConfig> => {
    const result = await api.get<
      SuccessEnvelope<IdentityVerificationConfig>
    >(`/admin/projects/${projectId}/identity-verification`);
    return result.data;
  },

  updateIdentityVerificationConfig: async (input: {
    projectId: string;
    mode: 'track_identify' | 'identify_only' | 'secure';
    issuer?: string | null;
    audience?: string | null;
    jwks_url?: string | null;
    algorithms?: string[];
  }): Promise<IdentityVerificationConfig> => {
    const { projectId, ...body } = input;
    const result = await api.post<
      SuccessEnvelope<IdentityVerificationConfig>
    >(`/admin/projects/${projectId}/identity-verification`, body);
    return result.data;
  },

  // --- Billing usage tile ---
  getActiveIdentifiedUsersUsage: async (params: {
    organizationId: string;
    period?: string;
  }): Promise<ActiveIdentifiedUsersUsage> => {
    const search = new URLSearchParams();
    search.set('organization_id', params.organizationId);
    if (params.period) search.set('period', params.period);
    const result = await api.get<SuccessEnvelope<ActiveIdentifiedUsersUsage>>(
      `/admin/usage/active-identified-users?${search.toString()}`,
    );
    return result.data;
  },
};
